import { api } from '@api'
import DOMPurify from 'dompurify'
import { type JSX, createResource } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { css } from 'styled-system/css'

type SimpleRichTextProps = {
  content: string
  class?: string
}

// --- ASYNC IMAGE COMPONENT ---
// Handles fetching the signed URL independently for each image
function AsyncImage(props: { src: string; alt: string; class?: string }) {
  const fetchSignedUrl = async (src: string) => {
    // If it's already a full web URL or base64, just return it
    if (src.startsWith('http') || src.startsWith('data:')) {
      return src
    }

    // Otherwise, assume it's a relative path in our bucket (e.g., "inline/image.jpg")
    const { data, error } = await api().storage
      .from('course-images')
      .createSignedUrl(src, 60 * 60 * 24) // 24 hours expiry

    if (error) {
      console.error('Failed to load image:', error.message)
      return '' // Or return a fallback placeholder image path here
    }

    return data.signedUrl
  }

  // createResource is perfect for this: it handles the loading state automatically
  const [imgSrc] = createResource(props.src, fetchSignedUrl)

  return (
    <img
      src={imgSrc() || ''}
      alt={props.alt}
      class={props.class}
      loading="lazy" // Good practice for performance on rich text with many images
    />
  )
}

function convertHtmlToJsx(html: string): JSX.Element[] {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  function walk(node: ChildNode): JSX.Element | string | null {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent
    if (node.nodeType !== Node.ELEMENT_NODE) return null

    const el = node as HTMLElement
    const tag = el.tagName.toLowerCase()

    // 🖼️ Inject our custom AsyncImage instead of the native img
    if (tag === 'img') {
      const src = el.getAttribute('src') ?? ''
      const alt = el.getAttribute('alt') ?? ''
      return (
        <AsyncImage
          src={src}
          alt={alt}
          class={css({
            maxWidth: '100%',
            height: 'auto',
            borderRadius: 'md',
            my: 4,
          })}
        />
      )
    }

    const props = Object.fromEntries(
      [...el.attributes].map((attr) => [attr.name, attr.value])
    )
    const children = Array.from(el.childNodes).map(walk).filter(Boolean)

    return (
      <Dynamic component={tag} {...props}>
        {children}
      </Dynamic>
    )
  }

  return Array.from(doc.body.childNodes).map(walk).filter(Boolean) as JSX.Element[]
}

export default function SimpleRichText(props: SimpleRichTextProps): JSX.Element {
  const safeHtml = () =>
    DOMPurify.sanitize(props.content || '', {
      USE_PROFILES: { html: true },
    })

  return (
    <div
      class={
        props.class ||
        css({
          textStyle: 'xl',
          mb: 6,
          '& strong': { fontWeight: 'semibold' },
          '& em': { fontStyle: 'italic' },
          '& p': { mb: 2 },
          '& a': { color: 'jade.10', textDecoration: 'underline' },
        })
      }
    >
      {convertHtmlToJsx(safeHtml())}
    </div>
  )
}
